import type { ReactNode } from 'react'
import type { PopulationComparison } from '@behuman/core-contract'
import { Paper } from '@mantine/core'
import { ChangeText } from '../components/ChangeText'
import { InfoIcon } from '../components/InfoIcon'
import { SectionCard } from '../components/SectionCard'
import { formatCount, formatDays } from '../format'
import type { usePopulationSummary } from '../hooks'

/** The changes' label, naming the comparison actually chosen. */
const VS: Record<PopulationComparison, string> = {
  'previous-period': 'vs previous period',
  'same-dates-last-year': 'vs same dates last year',
}

function Metric({
  label,
  definition,
  value,
  children,
}: {
  label: string
  definition: string
  value: string
  children?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1 px-5 py-4" aria-label={label} role="group">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase">
          {label}
        </span>
        <InfoIcon label={label} definition={definition} />
      </div>
      <div className="text-3xl font-semibold text-slate-900 tabular-nums">{value}</div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  )
}

/** Attributed patients, high-risk patients, open care gaps and the median time to resolve. */
export function HeadlineMetrics({
  summary,
  comparison,
}: {
  summary: ReturnType<typeof usePopulationSummary>
  comparison: PopulationComparison
}) {
  const data = summary.data
  const vs = VS[comparison]
  return (
    <SectionCard title="Headline metrics" query={summary} frame="open" hideTitle>
      {data && (
        <Paper withBorder radius="lg">
          <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x">
            <Metric
              label="Attributed patients"
              definition={data.attributedPatients.definition}
              value={formatCount(data.attributedPatients.value)}
            >
              <span className="text-xs text-slate-500">In your organizations today</span>
            </Metric>
            <Metric
              label="High-risk patients"
              definition={`${data.highRiskPatients.definition} ${data.riskLevelDefinition}`}
              value={formatCount(data.highRiskPatients.value)}
            >
              <ChangeText
                value={data.highRiskPatients.changePercent}
                unit="percent"
                better="down"
                label={vs}
              />
              <span className="text-xs font-medium text-red-600">
                {formatCount(data.highRiskPatients.newInPeriod)} newly high-risk
              </span>
            </Metric>
            <Metric
              label="Open care gaps"
              definition={data.openCareGaps.definition}
              value={formatCount(data.openCareGaps.value)}
            >
              <ChangeText
                value={data.openCareGaps.changePercent}
                unit="percent"
                better="down"
                label={vs}
              />
            </Metric>
            <Metric
              label="Median time to resolve"
              definition={data.closureTime.definition}
              value={formatDays(data.closureTime.current.medianDays)}
            >
              <ChangeText
                value={data.closureTime.changeDays}
                unit="days"
                better="down"
                label={vs}
              />
            </Metric>
          </div>
        </Paper>
      )}
    </SectionCard>
  )
}
